import prodConfig from "./config.prod.json";
import gammaConfig from "./config.gamma.json";
import betaConfig from "./config.beta.json";
import devConfig from "./config.dev.json";
import sandboxConfig from "./config.sandbox.json";
import localConfig from "./config.local.json";

import { OrderingConfig, SiteConfiguration } from "configuration/site-configuration";
import { Stage } from "@amzn/uxm-aws-ui-components-react/lib/src/types";

/**
 * Prefix used for admin hostnames across all environments.
 */
export const DOMAIN_PREFIX = "itse";

/**
 * Base domain name for all IT Shop environments.
 */
export const BASE_DOMAIN = "shop.it.a2z.com";

/**
 * Builds a domain URL by combining the domain prefix, stage, and base domain.
 *
 * @param stage - The environment stage (empty string for production)
 * @returns The complete domain URL for the specified stage
 */
function buildDomain(stage: string): string {
    return `${DOMAIN_PREFIX}${stage ? `.${stage}` : ""}.${BASE_DOMAIN}`;
}

/**
 * Extracts the environment stage from the current hostname.
 * Supports detection of gamma, beta, dev, sandbox, and local environments.
 *
 * @returns The detected stage ('gamma', 'beta', 'dev') or empty string for production
 */
export function retrieveStageFromHostname(): string {
    const hostname = location.hostname;
    const match = hostname.match(/^(?:local|itse)\.([^.]+)\./);

    if (match) {
        const stage = match[1].toLowerCase();
        if (["gamma", "beta", "dev"].includes(stage)) {
            return stage;
        }

        if (stage == "sandbox") return "dev";
    }

    if (hostname.startsWith("local.")) return "dev";

    return "";
}

/**
 * Converts the hostname stage to a UXM-compatible stage format.
 * Maps 'dev' to 'beta' and empty string to 'prod' for UXM tracking.
 *
 * @returns UXM stage identifier for metrics tracking
 */
export function retrieveUxmStage(): Stage {
    const stage = retrieveStageFromHostname();

    if (stage === "dev") return "beta";

    return (stage === "" ? "prod" : stage) as Stage;
}

/**
 * Permission constants used for user authorization checks.
 * These values match the permission keys returned by the user permissions API.
 */
export const PERMISSIONS = {
    /** Permission key for software procurement operations */
    SOFTWARE_PROCUREMENT: "software_procurement",
    /** Permission key for IT support engineer hardware restock operations */
    ITSE_HARDWARE_RESTOCK: "itse_hardware_restock",
    /** Permission key for IT to view all the hardware catalog */
    ITSE_FULL_CATALOG_ACCESS: "itsehamFullCatalogAccess",
};

/**
 * Production environment domain URL.
 */
export const PROD_DOMAIN = buildDomain("");

/**
 * Gamma environment domain URL.
 */
export const GAMMA_DOMAIN = buildDomain("gamma");

/**
 * Beta environment domain URL.
 */
export const BETA_DOMAIN = buildDomain("beta");

/**
 * Development environment domain URL.
 */
export const DEV_DOMAIN = buildDomain("dev");

/**
 * Sandbox environment domain URL.
 */
export const SANDBOX_DOMAIN = buildDomain("sandbox");

/**
 * Local development environment domain URL.
 */
export const LOCAL_DOMAIN = buildDomain("local");

/**
 * Determines the appropriate configuration based on the current hostname.
 * Falls back to local configuration for unrecognized hostnames.
 *
 * @returns The site configuration object for the current environment
 */
function getConfigByEnv(): SiteConfiguration {
    switch (location.hostname) {
        case PROD_DOMAIN:
            return prodConfig;
        case GAMMA_DOMAIN:
            return gammaConfig;
        case BETA_DOMAIN:
            return betaConfig;
        case DEV_DOMAIN:
            return devConfig;
        case SANDBOX_DOMAIN:
            return sandboxConfig;
        default:
            return localConfig;
    }
}

/**
 * Current environment configuration loaded based on hostname detection.
 */
export const config = getConfigByEnv();

/**
 * Base URL for all API endpoints in the current environment.
 */
export const API_BASE_URL = config.apiBaseUrl;

/**
 * Catalogical service endpoint URL for ITSE hardware items.
 */
export const CATALOGICAL_ENDPOINT = `${API_BASE_URL}/cat-itseham`;

/**
 * Catalogical hardware items endpoint path for API requests.
 */
export const CATALOGICAL_HARDWARE_ENDPOINT = "/items/itseham/";

/**
 * Catalogical service endpoint URL for hardware categories.
 */
export const CATALOGICAL_CATEGORIES_ENDPOINT = `${API_BASE_URL}/cat-itsehamaux`;

/**
 * Catalogical hardware auxiliary items endpoint path for API requests.
 */
export const CATALOGICAL_HARDWARE_AUX_ENDPOINT = "/items/itsehamaux/";

/**
 * Ordering configuration settings for the current environment.
 */
export const ORDERING_CONFIG: OrderingConfig = config.ordering;
